import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Star, Lightning, CaretDoubleUp, Sparkle, CheckCircle } from "@phosphor-icons/react";

// The 3 recommended packages shown right after a listing is published.
// Descriptions map onto the packages returned from /api/settings/doping-packages.
const RECOMMENDED = [
  {
    id: "homepage_vitrin",
    icon: Star,
    tone: "bg-amber-50 text-amber-800",
    ring: "ring-amber-200",
    tagLabel: "En popüler",
    benefits: [
      "Ana sayfa vitrininde büyük görselli slot",
      "Ortalama 5× daha fazla tıklanma",
      "Öne çıkan koleksiyonlarda öncelik",
    ],
  },
  {
    id: "urgent_badge",
    icon: Lightning,
    tone: "bg-red-50 text-[#B54037]",
    ring: "ring-red-200",
    tagLabel: "Hızlı satış",
    benefits: [
      "Kırmızı “Acil” etiketi ile dikkat çeker",
      "Acil satışlar bölümüne dahil olur",
      "Alıcı psikolojisinde aciliyet hissi",
    ],
  },
  {
    id: "top_row",
    icon: CaretDoubleUp,
    tone: "bg-slate-100 text-slate-700",
    ring: "ring-slate-200",
    tagLabel: "Uygun fiyatlı",
    benefits: [
      "Arama ve keşfet sıralamasında üst sıra",
      "Kategori sayfalarında ilk 3’te görünme",
      "En ucuz sıralama avantajı",
    ],
  },
];

const STORAGE_PREFIX = "ks_upsell_seen_";

export default function DopingUpsellModal({ listingId, listingTitle, onClose }) {
  const [packages, setPackages] = useState({});   // id → package details
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/settings/doping-packages").then(({ data }) => {
      const map = {};
      data.forEach((p) => { map[p.id] = p; });
      setPackages(map);
    });
    // Guard against React 18 StrictMode double-invoke: set the flag FIRST,
    // fire the impression event only if not previously seen this session.
    const key = STORAGE_PREFIX + listingId;
    let alreadySeen = false;
    try {
      alreadySeen = !!localStorage.getItem(key);
      if (!alreadySeen) localStorage.setItem(key, "1");
    } catch { /* ignore */ }
    if (!alreadySeen) {
      api.post("/growth/events", { kind: "upsell_shown", listing_id: listingId }).catch(() => {});
    }
  }, [listingId]);

  const buy = async (pkgId) => {
    setBusyId(pkgId);
    try {
      await api.post("/growth/events", {
        kind: "upsell_selected", listing_id: listingId, doping_type: pkgId,
      }).catch(() => {});
      const { data } = await api.post("/payments/doping-checkout", {
        auction_id: listingId, doping_type: pkgId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başlatılamadı");
      setBusyId(null);
    }
  };

  const dismiss = () => {
    api.post("/growth/events", { kind: "upsell_dismissed", listing_id: listingId }).catch(() => {});
    onClose();
  };

  const items = RECOMMENDED.map((r) => ({ ...r, pkg: packages[r.id] }))
                            .filter((r) => r.pkg);

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={dismiss}
      data-testid="doping-upsell-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full soft-shadow-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 md:p-8 pb-2 relative">
          <button
            onClick={dismiss}
            data-testid="upsell-close"
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-50 transition-colors"
            aria-label="Kapat"
          >
            <X size={16} weight="bold" />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={16} weight="fill" className="text-emerald-500" />
            <span className="text-[10px] uppercase tracking-[0.28em] font-medium text-emerald-700">
              İlanın yayında
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 leading-tight">
            İlanını ilk 24 saatte öne çıkar
          </h2>
          <p className="text-sm text-slate-500 mt-3 leading-relaxed max-w-2xl">
            Yeni yayınlanan ilanlar ilk saatlerde daha çok dikkat çeker. Öne çıkarma paketi ile
            daha fazla teklif ve mesaj alabilirsin. Aşağıdaki öneriler senin için özenle seçildi.
          </p>
          {listingTitle && (
            <p className="mt-4 text-xs text-slate-400 truncate">
              <span className="uppercase tracking-[0.22em] font-medium">İlan:</span>{" "}
              <span className="text-slate-600 font-medium">{listingTitle}</span>
            </p>
          )}
        </div>

        {/* Package grid */}
        <div
          className="p-6 md:p-8 pt-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          data-testid="upsell-package-grid"
        >
          {items.length === 0 && (
            <p className="col-span-full text-center text-sm text-slate-400 py-10">
              Paketler yükleniyor…
            </p>
          )}
          {items.map(({ id, icon: Icon, tone, ring, tagLabel, benefits, pkg }) => {
            const busy = busyId === id;
            return (
              <div
                key={id}
                data-testid={`upsell-pkg-${id}`}
                className={`relative rounded-xl bg-white border border-slate-100 p-5 flex flex-col ring-0 hover:ring-4 ${ring} hover:-translate-y-0.5 transition-all duration-300`}
              >
                {id === "homepage_vitrin" && (
                  <span className="absolute -top-2.5 left-5 bg-slate-900 text-white text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full">
                    Önerilen
                  </span>
                )}
                <div className={`inline-flex self-start items-center gap-1.5 ${tone} text-[10px] uppercase tracking-[0.2em] font-medium px-2.5 py-1 rounded-full mb-4`}>
                  <Icon size={11} weight="fill" /> {tagLabel}
                </div>
                <h3 className="font-display text-lg font-medium tracking-tight text-slate-900 leading-snug">
                  {pkg.label}
                </h3>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-mono-tight text-3xl font-medium text-slate-900">₺{pkg.price}</span>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-medium text-slate-400">
                    · {pkg.days} gün
                  </span>
                </div>

                <ul className="mt-4 space-y-2 text-xs text-slate-600 leading-relaxed">
                  {benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Sparkle size={12} weight="fill" className="text-slate-400 mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => buy(id)}
                  disabled={busy}
                  data-testid={`upsell-buy-${id}`}
                  className="mt-5 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60"
                >
                  {busy ? "Yönlendiriliyor…" : "Hemen Öne Çıkar"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 md:px-8 pb-6 md:pb-8 pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t divider">
          <p className="text-xs text-slate-500 leading-relaxed max-w-md">
            💡 Doping süresi biten ilanların etiketi otomatik olarak düşer. Paketleri daha sonra da panelinden satın alabilirsin.
          </p>
          <button
            type="button"
            onClick={dismiss}
            data-testid="upsell-dismiss"
            className="text-sm font-medium text-slate-500 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-900 px-5 py-2.5 rounded-lg transition-colors"
          >
            Şimdi değil
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper for callers to check if the upsell has already been shown for a listing
export function upsellAlreadyShown(listingId) {
  try {
    return !!localStorage.getItem(STORAGE_PREFIX + listingId);
  } catch {
    return false;
  }
}
